import type { Practitioner } from "@medplum/fhirtypes";

/** Approved BMH-649 copy revision; increment only with product-approved wording changes. @agentic connections */
export const SMS_TEMPLATE_CATALOG_VERSION = 1;

/**
 * Fixed catalog approved August 25, 2026; Notion source revision September 29, 2026.
 * Source: docs/prds/2026-09-30-prd-sms-templates.source.md, section 4.4.
 * @agentic connections
 */
export const SMS_TEMPLATES = [
  {
    key: "nudge-1",
    label: "Nudge 1",
    body: "Hi! This is [User] from [Clinician]’s Care Team. I’m checking in to see if you had a chance to look over the resource we sent over. Let us know if you have any questions or thoughts, or run into any issues getting connected!",
    isSurvey: false,
  },
  {
    key: "nudge-2",
    label: "Nudge 2",
    body: "Hi, this is [User] again from [Clinician]’s Care Team. Let us know if we can help you get scheduled or clear up any questions about the resource we shared!",
    isSurvey: false,
  },
  {
    key: "last-outreach",
    label: "Last Outreach",
    body: "Hi! This is [User] from [Clinician]’s Care Team. I wanted to reach out one last time about the resource we shared. Please don’t hesitate to reach out at any point if we can offer any support!",
    isSurvey: false,
  },
  {
    key: "scheduled",
    label: "Scheduled",
    body: "Hi, I just wanted to check in to see if you were able to get scheduled okay? Let us know if you run into any issues or if we can offer any support!",
    isSurvey: false,
  },
  {
    key: "follow-up",
    label: "Follow Up",
    body: "Hi, this is [User] from [Clinician]’s Care Team. I wanted to check in to see how your appointment went. Did it feel like a good fit? Let us know if you have any questions or if there’s anything we can help with!",
    isSurvey: false,
  },
  {
    key: "check-in",
    label: "Check In",
    body: "Hi, this is [User] from [Clinician]’s Care Team. I just wanted to check in to see how everything is going? Feel free to reach out at any point! We’re here if you need any support, have questions, or want help getting connected to care.",
    isSurvey: false,
  },
  {
    key: "6-week",
    label: "6 Week",
    body: "Hi, it's your care team at Benchmark. It's been a few weeks since your first visit with [Clinician]. Want to see how you’ve progressed? This quick check-in takes a minute, and you'll see your results right after you finish:",
    isSurvey: true,
  },
  {
    key: "12-week",
    label: "12 Week",
    body: "Hi, your care team at Benchmark here again. It's been a few months since your first visit with [Clinician] - a good moment to check in on your progress. Answer a few quick questions and you'll see how your results compare to when you started:",
    isSurvey: true,
  },
  {
    key: "18-week",
    label: "18 Week",
    body: "Hi, just a check in from your care team at Benchmark. It's been a few months since you began working with [Clinician]. Answer a few quick questions, and you can see how you’re progressing:",
    isSurvey: true,
  },
  {
    key: "child",
    label: "Child",
    body: "Hi, it's your care team at Benchmark. It's been a few weeks since you and (child name)’s first visit with [Clinician]. Want to see how they’ve progressed? This quick check-in takes a minute, and you'll see their results right after you finish: ",
    isSurvey: true,
  },
] as const;

/** Stable template keys are safe operational codes, not resource identifiers. @agentic connections */
export type SMSTemplateKey = (typeof SMS_TEMPLATES)[number]["key"];

/** In-memory origin of a draft, restored with its text on undo. Never log renderedText. @agentic connections */
export type SMSTemplateProvenance = {
  templateKey: SMSTemplateKey;
  catalogVersion: number;
  renderedText: string;
};

/** The two supported substitutions; absent names use the approved fallbacks. @agentic connections */
type SMSTemplateNames = { senderName?: string; clinicianName?: string };

/** Reads only a structured first given name from a sender or assigned clinician practitioner. @agentic connections */
export const getSMSPractitionerFirstName = (
  practitioner: Practitioner | undefined,
): string | undefined => {
  const name =
    practitioner?.name?.find((entry) => entry.use === "official") ??
    practitioner?.name?.[0];
  return name?.given?.[0]?.trim() || undefined;
};

/** Renders approved plain text without interpreting replacement characters in names. @agentic connections */
export const renderSMSTemplate = (
  key: SMSTemplateKey,
  names: SMSTemplateNames,
): string => {
  const template = SMS_TEMPLATES.find((entry) => entry.key === key)!;
  const sender = names.senderName?.trim();
  const clinician = names.clinicianName?.trim();
  const possessive = clinician
    ? `${clinician}${/s$/i.test(clinician) ? "’" : "’s"}`
    : undefined;

  if (template.body.includes("[User]") && (!sender || !clinician)) {
    const remainder = template.body.slice(
      template.body.indexOf("Care Team.") + "Care Team.".length,
    );
    const greeting = template.body.startsWith("Hi!")
      ? "Hi! This is"
      : "Hi, this is";
    if (sender) return `${greeting} ${sender}.${remainder}`;
    if (possessive) return `${greeting} ${possessive} Care Team.${remainder}`;
    return `Hi!${remainder}`;
  }

  return template.body
    .replaceAll("[User]", () => sender ?? "[User]")
    .replaceAll("[Clinician]’s", () => possessive ?? "[Clinician]’s")
    .replaceAll("[Clinician]", () => clinician ?? "[Clinician]");
};

/** Finds only the known manual placeholders, returning safe labels for validation. @agentic connections */
export const getSMSPlaceholders = (text: string): string[] => {
  const remaining: string[] = [];
  if (/\[\s*user\s*\]/i.test(text)) remaining.push("[User]");
  if (/\[\s*clinician\s*\]/i.test(text)) remaining.push("[Clinician]");
  if (/\(\s*child\s+name\s*\)/i.test(text)) remaining.push("(child name)");
  return remaining;
};
